import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import ChatContext from "./ChatContext";
import Conversations from "./Conversations";
import Messages from "./Messages";
import AppContext from "AppContext";
import ChatInitializerContext from "./ChatInitializerContext";
import Status from "components/common_components/Status";
import { getConversationStatusProps } from "./utils";

const HEADER_STATUS_STATES = ["active", "inactive", "initializing"];

function Chat({ isSingleConversation }) {
    const [messagePaginator, setMessagePaginator] = useState();
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const RETRY_TIME_IN_SECONDS = 5;
    const [retryingCountDown, setRetryingCountDown] = useState(RETRY_TIME_IN_SECONDS);
    const canLoadMoreMessages = messagePaginator && messagePaginator.hasPrevPage;
    const { conversationsGetter } = useContext(ChatInitializerContext);
    const {
        messaging: { twilioConversations, selectedConversation, messages, profile },
        setMessages,
        setSelectedConversation,
    } = useContext(AppContext);

    const connectionStates = {
        connecting: { text: "Connecting...", type: "default" },
        connected: { text: "Online", type: "success" },
        disconnecting: { text: "Disconnecting...", type: "default" },
        disconnected: { text: "Offline", type: "warning" },
        denied: { text: "Access denied!", type: "error" },
    };

    let retries = 0;
    const retry = (callbackFn) => {
        let interval;
        if (retries < 3) {
            setRetrying(true);
            retries++;
            interval = setInterval(() => setRetryingCountDown((count) => count - 1), 1000);
            setTimeout(() => {
                clearInterval(interval);
                callbackFn();
            }, RETRY_TIME_IN_SECONDS * 1000);
        } else {
            setRetrying(false);
            interval && clearInterval(interval);
        }
        setRetryingCountDown(RETRY_TIME_IN_SECONDS);
    };

    const getExistingMessagesAsync = async (conversation) => {
        try {
            setMessages([]);
            setLoadingMessages(true);
            const messagesCount = await conversation.getMessagesCount();
            if (messagesCount && Number(messagesCount) > 0) {
                const messagePaginator = await conversation.getMessages(50);
                setMessagePaginator(messagePaginator);
                setMessages(messagePaginator.items);
            }
        } catch (error) {
            console.error("Couldn't fetch messages. Retrying...", error);
            retry(() => getExistingMessagesAsync(conversation));
        } finally {
            setLoadingMessages(false);
        }
    };

    const onConversationSelected = (conversation) => {
        setSelectedConversation(conversation);
        getExistingMessagesAsync(conversation);
    };

    const loadMoreMessages = async () => {
        setLoadingMoreMessages(true);
        const newMessagePaginator = await messagePaginator.prevPage();
        setMessagePaginator(newMessagePaginator);
        setMessages((prev) => [...newMessagePaginator.items, ...prev]);
        setTimeout(() => {
            document.getElementById("load-more").scrollIntoView({ behavior: "smooth" });
        }, 100);
        setLoadingMoreMessages(false);
    };

    return (
        <ChatContext.Provider
            value={{
                conversations: conversationsGetter(),
                messages,
                setMessages,
                selectedConversation,
                setSelectedConversation,
                isSingleConversation,
                profile,
            }}
        >
            <div className={`chat-container${isSingleConversation ? " single-conversation" : ""}`}>
                <header className="header">
                    <div className="state">
                        <span className={`state-dot state-${connectionStates[twilioConversations.connectionState].type}`} />
                        <span className="state-text">{connectionStates[twilioConversations.connectionState].text}</span>
                    </div>
                    {selectedConversation && HEADER_STATUS_STATES.includes(selectedConversation.state?.current) && (
                        <div className="conversation-state">
                            <Status {...getConversationStatusProps(selectedConversation.state.current)} />
                        </div>
                    )}
                </header>
                {twilioConversations.initialized && (
                    <div className="row">
                        <Conversations onConversationSelected={onConversationSelected} />
                        {selectedConversation ? (
                            <Messages
                                loading={loadingMessages}
                                retrying={retrying}
                                retryingCountDown={retryingCountDown}
                                loadMoreMessages={canLoadMoreMessages ? loadMoreMessages : undefined}
                                loadingMoreMessages={loadingMoreMessages}
                            />
                        ) : (
                            <div className="no-conversation-selected"> Select a conversation to see its messages!</div>
                        )}
                    </div>
                )}
            </div>
        </ChatContext.Provider>
    );
}

Chat.propTypes = {
    isSingleConversation: PropTypes.bool,
};

export default Chat;
